import { type Comments as CommentsProps } from '@web/graphql-components'
import { Box, SimpleGrid } from '@web/components/atoms/Layout'
import { DialogBody, DialogCloseTrigger, DialogHeader } from '@web/components/atoms/Modal'
import { Body2 } from '@web/components/atoms/Typography'
import { commentsDocument, getCommentsQueryOptions, mapComment } from './utils'
import useGraphQLClient from 'apps/fe-donation/libs/hooks/useGraphQLClient'
import { useInfiniteQuery } from '@tanstack/react-query'
import { CommentsQuery } from '@web/graphql-queries/graphql'
import Comment from '@web/components/page-components/Comments/Comment'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { usePageStore } from 'apps/fe-donation/libs/hooks/usePageStore'
import { useInView } from 'react-intersection-observer'
import { useEffect } from 'react'
import { Skeleton } from '@web/components/atoms/Skeleton'

dayjs.extend(relativeTime)
const PAGE_SIZE = 21

const ViewAllDialog = (props: CommentsProps) => {
  const campaignDomain = usePageStore((state) => state.campaignDomain) ?? ''
  const options = getCommentsQueryOptions(props)
  const graphQLClient = useGraphQLClient()
  const { data, isFetchingNextPage, isFetching, fetchNextPage, isPending, hasNextPage } = useInfiniteQuery({
    queryKey: ['comments-infinite', options],
    queryFn: async ({ pageParam }) =>
      graphQLClient<CommentsQuery>({
        document: commentsDocument,
        options: {
          query: { ...options, pagination: { pageNumber: pageParam, pageSize: PAGE_SIZE } },
        },
      }),
    getNextPageParam: (lastPage, _, lastPageParam) => {
      const totalRecords = lastPage.comments?.totalRecords ?? 0
      const totalPages = Math.ceil(totalRecords / PAGE_SIZE)

      return lastPageParam > totalPages ? undefined : lastPageParam + 1
    },
    refetchOnWindowFocus: false,
    initialPageParam: 0,
  })

  const comments = data?.pages?.flatMap((page) => page.comments?.comments ?? []) ?? []

  const { ref, inView } = useInView({
    threshold: 0.5,
  })

  useEffect(() => {
    if (!inView || isFetchingNextPage || isFetching || !hasNextPage) {
      return
    }

    fetchNextPage()
  }, [inView])

  return (
    <>
      <DialogHeader>
        <Body2 as="strong">Total Supporters: {data?.pages[0]?.comments?.totalRecords}</Body2>
        <DialogCloseTrigger />
      </DialogHeader>
      <DialogBody>
        <SimpleGrid columns={[1, 3]} gap="16px">
          {isPending && Array.from({ length: 12 }).map((_, i) => <Skeleton key={i} w="270px" h="112px" />)}
          {comments.map((comment, i) => (
            <Box key={comment.id} ref={i === comments.length - 6 && hasNextPage ? ref : undefined}>
              <Comment
                {...mapComment(comment, props, campaignDomain)}
                styles={{
                  height: '112px',
                  fundraiserLinkColor: props.styles?.fundraiserLinkColor,
                  width: '100%',
                }}
                withPopover={true}
              />
            </Box>
          ))}
          {isFetchingNextPage && Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} w="100%" h="112px" />)}
        </SimpleGrid>
      </DialogBody>
    </>
  )
}

export default ViewAllDialog
